<template>
  <div class="home-page">
    <!-- Header -->
    <header class="header">
      <div class="header-container">
        <div class="header-content">
          <div class="logo">
            <img v-if="siteSettings.site_logo" :src="`/storage/settings/${siteSettings.site_logo}`" alt="Site Logo" class="site-logo" />
            <VsxIcon v-else iconName="Chart" :size="32" color="#BB86FC" />
            <span class="logo-text">{{ siteSettings.site_name }}</span>
          </div>
          
          <nav class="nav-menu">
            <a href="#home" class="nav-link">خانه</a>
            <a href="#services" class="nav-link">خدمات</a>
            <a href="#plans" class="nav-link">پلن‌ها</a>
            <a href="#download" class="nav-link">دانلود</a>
            <a href="#about" class="nav-link">درباره ما</a>
          </nav>
          
          <div class="header-actions">
            <button class="btn-theme" @click="toggleTheme">
              <VsxIcon :iconName="theme === 'light' ? 'Moon' : 'Sun'" :size="20" :color="theme === 'light' ? '#3C278E' : '#fff'" />
            </button>
            <button class="btn-login" @click="goToLogin">
              <VsxIcon iconName="Login" :size="18" color="#BB86FC" />
              <span>ورود</span>
            </button>
            <button class="btn-register" @click="goToRegister">
              <VsxIcon iconName="UserAdd" :size="18" color="#fff" />
              <span>ثبت‌نام</span>
            </button>
          </div>
          
          <button class="mobile-menu-btn" @click="toggleMobileMenu">
            <VsxIcon :iconName="mobileMenuOpen ? 'CloseSquare' : 'Menu'" :size="24" color="#fff" />
          </button>
        </div>
      </div>
      
      <!-- Mobile Menu -->
      <div class="mobile-menu" :class="{ 'mobile-menu-open': mobileMenuOpen }">
        <!-- Header -->
        <div class="mobile-menu-header">
          <div class="mobile-menu-brand">
            <img v-if="siteSettings.site_logo" :src="`/storage/settings/${siteSettings.site_logo}`" alt="Site Logo" class="site-logo-small" />
            <VsxIcon v-else iconName="Chart" :size="24" color="#BB86FC" />
            <span>{{ siteSettings.site_name }}</span>
          </div>
          <button class="mobile-menu-close" @click="closeMobileMenu">
            <VsxIcon iconName="CloseSquare" :size="24" color="#fff" />
          </button>
        </div>
        
        <!-- Navigation -->
        <nav class="mobile-menu-nav">
          <a href="#home" class="mobile-menu-item" @click="closeMobileMenu">
            <div class="mobile-menu-icon">
              <VsxIcon iconName="Home" :size="20" color="#BB86FC" />
            </div>
            <span>خانه</span>
          </a>
          <a href="#services" class="mobile-menu-item" @click="closeMobileMenu">
            <div class="mobile-menu-icon">
              <VsxIcon iconName="Setting2" :size="20" color="#4BC0C0" />
            </div>
            <span>خدمات</span>
          </a>
          <a href="#plans" class="mobile-menu-item" @click="closeMobileMenu">
            <div class="mobile-menu-icon">
              <VsxIcon iconName="Crown" :size="20" color="#5584FF" />
            </div>
            <span>پلن‌ها</span>
          </a>
          <a href="#download" class="mobile-menu-item" @click="closeMobileMenu">
            <div class="mobile-menu-icon">
              <VsxIcon iconName="DocumentDownload" :size="20" color="#FF6B6B" />
            </div>
            <span>دانلود</span>
          </a>
          <a href="#about" class="mobile-menu-item" @click="closeMobileMenu">
            <div class="mobile-menu-icon">
              <VsxIcon iconName="InfoCircle" :size="20" color="#4ECDC4" />
            </div>
            <span>درباره ما</span>
          </a>
        </nav>
        
        <!-- Actions -->
        <div class="mobile-menu-actions">
          <button class="mobile-menu-btn mobile-menu-btn-theme" @click="toggleTheme">
            <VsxIcon :iconName="theme === 'light' ? 'Moon' : 'Sun'" :size="18" :color="theme === 'light' ? '#3C278E' : '#fff'" />
            <span>{{ theme === 'light' ? 'حالت تاریک' : 'حالت روشن' }}</span>
          </button>
          <button class="mobile-menu-btn mobile-menu-btn-login" @click="goToLogin">
            <VsxIcon iconName="Login" :size="18" color="#BB86FC" />
            <span>ورود</span>
          </button>
          <button class="mobile-menu-btn mobile-menu-btn-register" @click="goToRegister">
            <VsxIcon iconName="UserAdd" :size="18" color="#fff" />
            <span>ثبت‌نام</span>
          </button>
        </div>
      </div>
    </header>

    <!-- Animated Background -->
    <div class="animated-bg">
      <div class="floating-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
        <div class="shape shape-4"></div>
        <div class="shape shape-5"></div>
      </div>
    </div>

    <!-- Hero Section -->
    <section id="home" class="hero-section">
      <div class="container">
        <div class="hero-content">
          <div class="hero-text">
            <div class="hero-badge" v-if="heroContent.badge">
              <VsxIcon iconName="Star1" :size="16" color="#FFD700" />
              <span>{{ heroContent.badge }}</span>
            </div>
            
            <h1 class="hero-title">
              <span class="gradient-text">{{ siteSettings.site_name }}</span>
              <br>
              <span class="hero-subtitle">{{ heroContent.subtitle }}</span>
            </h1>
            
            <p class="hero-description">
              {{ heroContent.description }}
            </p>
            
            <div class="hero-stats">
              <div class="stat-item" v-for="(stat, index) in heroContent.stats" :key="index">
                <div class="stat-number">{{ stat.number }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>
            
            <div class="hero-buttons">
              <button class="btn-primary" @click="scrollToServices">
                <VsxIcon iconName="ArrowDown2" :size="20" color="#fff" />
                <span>شروع سرمایه‌گذاری</span>
                <div class="btn-glow"></div>
              </button>
              <button class="btn-secondary" @click="downloadApp">
                <VsxIcon iconName="Mobile" :size="20" color="#BB86FC" />
                <span>دانلود اپلیکیشن</span>
              </button>
            </div>  
          </div>
          
          <div class="hero-visual">
      
            
            <div class="floating-card card-1">
              <VsxIcon iconName="DollarSquare" :size="24" color="#4BC0C0" />
              <div class="card-text">
                <h4>سود روزانه</h4>
              </div>
            </div>
            
            <div class="floating-card card-2">
              <VsxIcon iconName="ShieldTick" :size="24" color="#FF6B6B" />
              <div class="card-text">
                <h4>امنیت بالا</h4>
                <p>بلاک‌چین</p>
              </div>
            </div>
            
            <div class="floating-card card-3">
              <VsxIcon iconName="UserAdd" :size="24" color="#5584FF" />
              <div class="card-text">
                <h4>پاداش دعوت</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Section -->
    <section id="services" class="services-section" ref="servicesSection">
      <div class="container">
        <div class="section-header">
          <div class="section-badge" v-if="services.length > 0">
            <VsxIcon iconName="Crown" :size="16" color="#FFD700" />
            <span>خدمات پیشرفته</span>
          </div>
          <h2>خدمات ما</h2>
          <p>با تکنولوژی‌های نوین، تجربه‌ای متفاوت از سرمایه‌گذاری</p>
        </div>
        
        <div class="services-showcase">
          <div class="service-main">
            <div class="service-card main-service">
              <div class="service-visual">
                <div class="service-icon-large">
                  <VsxIcon iconName="Chart" :size="48" color="#BB86FC" />
                </div>
                <div class="service-animation">
                  <div class="pulse-ring"></div>
                  <div class="pulse-ring"></div>
                  <div class="pulse-ring"></div>
                </div>
              </div>
              <div class="service-content">
                <h3>سرمایه‌گذاری هوشمند</h3>
                <p>با استفاده از الگوریتم‌های پیشرفته و هوش مصنوعی، بهترین فرصت‌های سرمایه‌گذاری را شناسایی و مدیریت می‌کنیم</p>
                <div class="service-stats">
                  <div class="stat">
                    <span class="stat-value">99.9%</span>
                    <span class="stat-label">دقت تحلیل</span>
                  </div>
                  <div class="stat">
                    <span class="stat-value">24/7</span>
                    <span class="stat-label">نظارت</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="services-sidebar">
            <div class="service-item" v-for="(service, index) in services" :key="index">
              <div class="service-icon">
                <VsxIcon :iconName="service.icon" :size="28" :color="service.color" />
              </div>
              <div class="service-info">
                <h4>{{ service.title }}</h4>
                <p>{{ service.description }}</p>
              </div>
              <div class="service-arrow">
                <VsxIcon iconName="ArrowRight3" :size="20" color="#BB86FC" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>


    <!-- Download App Section -->
    <section id="download" class="download-section">
      <div class="container">
        <div class="download-content">
          <div class="download-text">
            <div class="section-badge">
              <VsxIcon iconName="Mobile" :size="16" color="#FFD700" />
              <span>اپلیکیشن موبایل</span>
            </div>
            <h2>همیشه در دسترس باشید</h2>
            <p>با اپلیکیشن موبایل داده‌پردازان، سرمایه‌گذاری خود را در هر زمان و مکان مدیریت کنید</p>
            <div class="download-features">
              <div class="download-feature" v-for="(feature, index) in downloadFeatures" :key="index">
                <VsxIcon iconName="TickCircle" :size="20" color="#4BC0C0" />
                <span>{{ feature }}</span>
              </div>
            </div>
            <button class="download-btn" @click="downloadApp">
              <VsxIcon iconName="DocumentDownload" :size="24" color="#fff" />
              دانلود اپلیکیشن
            </button>
          </div>
                     <div class="download-visual">
             <div class="phone-mockup">
               <div class="phone-screen">
                 <img src="/img/application.png" alt="داده‌پردازان اپلیکیشن" class="app-screenshot" />
               </div>
             </div>
           </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="stats-section">
      <div class="container">
        <div class="stats-grid">
          <div class="stat-item" v-for="(stat, index) in stats" :key="index">
            <div class="stat-icon">
              <VsxIcon :iconName="stat.icon" :size="32" :color="stat.color" />
            </div>
            <div class="stat-number">{{ stat.number }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- About Section -->
    <section id="about" class="about-section">
      <div class="container">
        <div class="section-header">
          <div class="section-badge">
            <VsxIcon iconName="InfoCircle" :size="16" color="#FFD700" />
            <span>درباره ما</span>
          </div>
          <h2>درباره {{ siteSettings.site_name }}</h2>
          <p>ما متعهد به ارائه بهترین خدمات سرمایه‌گذاری با استفاده از فناوری‌های نوین هستیم</p>
        </div>
        
        <div class="about-content">
          <div class="about-text">
            <h3>ماموریت ما</h3>
            <p>{{ siteSettings.our_mission }}</p>
            
            <h3>ارزش‌های ما</h3>
            <ul>
              <li v-for="(value, index) in siteSettings.our_values" :key="index">{{ value }}</li>
            </ul>
          </div>
          
          <div class="about-stats">
            <div class="about-stat">
              <div class="stat-number">{{ siteSettings.foundation_year }}</div>
              <div class="stat-label">سال تاسیس</div>
            </div>
            <div class="about-stat">
              <div class="stat-number">{{ siteSettings.experts_count }}</div>
              <div class="stat-label">متخصص</div>
            </div>
            <div class="about-stat">
              <div class="stat-number">{{ siteSettings.countries_count }}</div>
              <div class="stat-label">کشور</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-content">
          <h2>آماده شروع سرمایه‌گذاری هستید؟</h2>
          <p>همین حالا ثبت‌نام کنید و از فرصت‌های طلایی سرمایه‌گذاری استفاده کنید</p>
          <div class="cta-buttons">
            <button class="btn-primary" @click="goToRegister">
              <VsxIcon iconName="UserAdd" :size="20" color="#fff" />
              ثبت‌نام رایگان
            </button>
            <button class="btn-secondary" @click="goToLogin">
              <VsxIcon iconName="Login" :size="20" color="#BB86FC" />
              ورود به حساب
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-content">
        <div class="container">
          <div class="footer-main">
          
            <p class="footer-description">
              {{ siteSettings.about_us }}
            </p>
          
          </div>

          <div class="footer-bottom">
            <div class="footer-bottom-content">
              <div class="copyright">
                <p>&copy; {{ new Date().getFullYear() }} {{ siteSettings.site_name }}. تمامی حقوق محفوظ است.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: 'HomePage',
  data() {
    return {
      mobileMenuOpen: false,
      siteSettings: {
        site_logo: null,
        site_name: '',
        site_email: '',
        site_phone: '',
        foundation_year: '',
        experts_count: '',
        countries_count: '',
        about_us: '',
        our_mission: '',
        referral_reward: '',
        our_values: []
      },
      // Dynamic content that will be loaded from settings
      heroContent: {
        badge: '',
        subtitle: '',
        description: '',
        stats: []
      },
      services: [
        {
          icon: 'Shield',
          color: '#4BC0C0',
          title: 'امنیت پیشرفته',
          description: 'امنیت بالا با فناوری بلاک‌چین'
        },
        {
          icon: 'UserAdd',
          color: '#5584FF',
          title: 'سیستم دعوت',
          description: 'پاداش دعوت تا ۵٪'
        },
        {
          icon: 'WalletMoney',
          color: '#FF6B6B',
          title: 'پرداخت سریع',
          description: 'برداشت آنی و بدون تاخیر'
        },
        {
          icon: 'Graph',
          color: '#BB86FC',
          title: 'تحلیل هوشمند',
          description: 'تحلیل بازار با هوش مصنوعی'
        }
      ],
      downloadFeatures: [
        'مدیریت سرمایه‌گذاری در هر زمان و مکان',
        'اطلاع‌رسانی لحظه‌ای از وضعیت سرمایه',
        'امکان برداشت و واریز آسان',
        'رابط کاربری ساده و کاربرپسند'
      ],
      stats: [
        { icon: 'User', color: '#BB86FC', number: '+۱۰۰K', label: 'کاربر فعال' },
        { icon: 'DollarSquare', color: '#4BC0C0', number: '$۵۰M+', label: 'حجم معاملات' },
        { icon: 'Global', color: '#5584FF', number: '+۲۵', label: 'کشور تحت پوشش' },
        { icon: 'Calendar', color: '#FF6B6B', number: '۳', label: 'سال فعالیت' }
      ]
    }
  },
  computed: {
    theme() {
      return this.$store.state.theme;
    }
  },
  watch: {
    theme: {
      immediate: true,
      handler() {
        this.loadTheme();
      }
    }
  },
  mounted() {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    this.loadSettings();
    this.loadHomepage();
    this.loadTheme();
  },
  beforeUnmount() {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  },
  methods: {
    loadTheme() {
      if (this.$store.state.theme === "light") {
        document.body.style.backgroundColor = "#f7f4ff";
        document.body.className = 'light';
      } else {
        document.body.style.backgroundColor = "#121212";
        document.body.className = 'dark';
      }
    },
    
    toggleTheme() {
      const newTheme = this.theme === 'light' ? 'dark' : 'light';
      this.$store.dispatch('changeTheme', newTheme);
      this.loadTheme();
    },
    
    async loadHomepage() {
      try {
        const { data } = await axios.get('/homepage/public');
        if (data?.success) {
          const d = data.data;
          // hero content
          if (d.hero) {
            this.heroContent = d.hero;
          }
          // services
          if (Array.isArray(d.services)) {
            this.services = d.services;
          }
          // stats
          if (Array.isArray(d.stats)) {
            this.stats = d.stats;
          }
          // site name override
          if (d.site?.site_name) {
            document.title = d.site.site_name;
          }
        }
      } catch (e) {
        console.error('Error loading homepage:', e);
      }
    },
    async loadSettings() {
      try {
        const response = await axios.get('/settings/public');
        
        if (response.data && response.data.success) {
          const data = response.data.data;
          
          // Update site settings with received data, providing fallbacks
          this.siteSettings = {
            site_logo: data.site_logo || null,
            site_name: data.site_name || 'داده‌پردازان',
            site_email: data.site_email || 'info@datapardazan.com',
            site_favicon: data.site_favicon || null,
            site_phone: data.site_phone || '+98 21 1234 5678',
            foundation_year: data.foundation_year || '2020',
            experts_count: data.experts_count || '50',
            countries_count: data.countries_count || '25',
            about_us: data.about_us || 'ما متعهد به ارائه بهترین خدمات سرمایه‌گذاری با استفاده از فناوری‌های نوین هستیم. با تیم متخصص و تجربه‌ای طولانی در زمینه مالی و تکنولوژی، فرصت‌های سرمایه‌گذاری امن و سودآور را برای شما فراهم می‌کنیم.',
            our_mission: data.our_mission || 'ماموریت ما ایجاد پلتفرمی امن و قابل اعتماد برای سرمایه‌گذاری است که با استفاده از فناوری‌های نوین، بهترین فرصت‌ها را در اختیار کاربران قرار دهد.',
            referral_reward: data.referral_reward || '5',
            our_values: []
          };
          
          // Parse our_values if it exists
          if (data.our_values) {
            try {
              this.siteSettings.our_values = JSON.parse(data.our_values);
            } catch (e) {
              console.error('Error parsing our_values:', e);
              // Provide default values if parsing fails
              this.siteSettings.our_values = [
                'شفافیت کامل در تمام معاملات',
                'امنیت بالا با استفاده از فناوری بلاک‌چین',
                'پشتیبانی 24/7 برای کاربران',
                'توسعه مستمر و بهبود خدمات'
              ];
            }
          } else {
            // Provide default values if no values are set
            this.siteSettings.our_values = [
              'شفافیت کامل در تمام معاملات',
              'امنیت بالا با استفاده از فناوری بلاک‌چین',
              'پشتیبانی 24/7 برای کاربران',
              'توسعه مستمر و بهبود خدمات'
            ];
          }
          
          // Update hero content if available
          if (data.hero_badge) this.heroContent.badge = data.hero_badge;
          if (data.hero_subtitle) this.heroContent.subtitle = data.hero_subtitle;
          if (data.hero_description) this.heroContent.description = data.hero_description;
          
          // Update document title
          document.title = this.siteSettings.site_name;
          
          // Update favicon if available
          if (this.siteSettings.site_favicon) {
            const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            link.href = `/storage/settings/${this.siteSettings.site_favicon}`;
            document.getElementsByTagName('head')[0].appendChild(link);
          }
          
          // Update referral reward in services
          const referralCard = this.services.find(s => s.icon === 'UserAdd');
          if (referralCard && data.referral_reward) {
            referralCard.description = `پاداش دعوت تا ${data.referral_reward}%`;
          }
          
          // Update stats with dynamic data
          if (data.foundation_year) {
            const yearStat = this.stats.find(s => s.icon === 'Calendar');
            if (yearStat) yearStat.number = data.foundation_year;
          }
          
          if (data.countries_count) {
            const countriesStat = this.stats.find(s => s.icon === 'Global');
            if (countriesStat) countriesStat.number = `+${data.countries_count}`;
          }
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        
        // Set default values if API call fails
        this.siteSettings = {
          site_logo: null,
          site_name: 'داده‌پردازان',
          site_email: 'info@datapardazan.com',
          site_phone: '+98 21 1234 5678',
          foundation_year: '2020',
          experts_count: '50',
          countries_count: '25',
          about_us: 'ما متعهد به ارائه بهترین خدمات سرمایه‌گذاری با استفاده از فناوری‌های نوین هستیم. با تیم متخصص و تجربه‌ای طولانی در زمینه مالی و تکنولوژی، فرصت‌های سرمایه‌گذاری امن و سودآور را برای شما فراهم می‌کنیم.',
          our_mission: 'ماموریت ما ایجاد پلتفرمی امن و قابل اعتماد برای سرمایه‌گذاری است که با استفاده از فناوری‌های نوین، بهترین فرصت‌ها را در اختیار کاربران قرار دهد.',
          referral_reward: '5',
          our_values: [
            'شفافیت کامل در تمام معاملات',
            'امنیت بالا با استفاده از فناوری بلاک‌چین',
            'پشتیبانی 24/7 برای کاربران',
            'توسعه مستمر و بهبود خدمات'
          ]
        };
      }
    },
    
    scrollToServices() {
      this.$refs.servicesSection.scrollIntoView({ behavior: 'smooth' });
      this.closeMobileMenu();
    },
    downloadApp() {
      const link = document.createElement('a');
      link.href = '/app-download-link';
      link.download = 'data-pardazan-app.apk';
      link.click();
      this.closeMobileMenu();
    },
    goToRegister() {
      this.$router.push('/register');
      this.closeMobileMenu();
    },
    goToLogin() {
      this.$router.push('/login');
      this.closeMobileMenu();
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      document.body.style.overflow = this.mobileMenuOpen ? 'hidden' : 'auto';
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
      document.body.style.overflow = 'auto';
    }
  }
}
</script>

<style scoped>
.home-page {
  min-height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  transition: all 0.3s ease;
}

/* Dark theme styles */
.dark .home-page {
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  color: #fff;
}

/* Light theme styles */
.light .home-page {
  background: linear-gradient(135deg, #f7f4ff 0%, #e8e2ff 50%, #d1c4e9 100%);
  color: #333;
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  backdrop-filter: blur(10px);
  z-index: 1000;
  padding: 15px 0;
  transition: all 0.3s ease;
}

/* Dark theme header */
.dark .header {
  background: rgba(15, 15, 35, 0.9);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Light theme header */
.light .header {
  background: rgba(247, 244, 255, 0.9);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.header-container {
  margin: 0 auto;
  padding: 0 10%;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.8rem;
  text-decoration: none;
  transition: color 0.3s ease;
}

/* Dark theme logo */
.dark .logo {
  color: #fff;
}

.light .logo {
  color: #333;
}

.logo-text {
  color: #BB86FC;
}

.site-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  margin-right: 8px;
}

.site-logo-small {
  width: 24px;
  height: 24px;
  object-fit: contain;
  margin-right: 8px;
}

.nav-menu {
  display: flex;
  gap: 40px;
  font-size: 1rem;
  transition: color 0.3s ease;
}

/* Dark theme nav */
.dark .nav-menu {
  color: #e0e0e0;
}

.light .nav-menu {
  color: #666;
}

.nav-link {
  text-decoration: none;
  transition: color 0.3s ease;
}

/* Dark theme nav links */
.dark .nav-link {
  color: #e0e0e0;
}

.light .nav-link {
  color: #666;
}

.nav-link:hover {
  color: #BB86FC;
}

.header-actions {
  display: flex;
  gap: 20px;
  align-items: center;
}

.btn-theme {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.btn-theme:hover {
  background: rgba(187, 134, 252, 0.2);
  transform: scale(1.1);
}

/* Dark theme button */
.dark .btn-theme {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.dark .btn-theme:hover {
  background: rgba(187, 134, 252, 0.3);
  border-color: #BB86FC;
}

/* Light theme button */
.light .btn-theme {
  background: rgba(60, 39, 142, 0.1);
  border: 1px solid rgba(60, 39, 142, 0.2);
}

.light .btn-theme:hover {
  background: rgba(60, 39, 142, 0.2);
  border-color: #3C278E;
}

.btn-login, .btn-register {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 50px;
  font-size: 0.9rem;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'iranyekanB', sans-serif;
  position: relative;
  overflow: hidden;
}

.btn-login {
  background: rgba(255, 255, 255, 0.1);
  color: #BB86FC;
  border: 2px solid rgba(187, 134, 252, 0.3);
  backdrop-filter: blur(10px);
}

.btn-login:hover {
  background: rgba(187, 134, 252, 0.1);
  border-color: #BB86FC;
  transform: translateY(-2px);
}

.btn-register {
  background: linear-gradient(45deg, #BB86FC, #4BC0C0);
  color: #fff;
  box-shadow: 0 10px 30px rgba(187, 134, 252, 0.3);
}

.btn-register:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 40px rgba(187, 134, 252, 0.4);
}

.btn-glow {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn-register:hover .btn-glow {
  left: 100%;
}

.mobile-menu-btn {
  display: none !important; /* Force hide on desktop */
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
}

.mobile-menu-btn:hover {
  opacity: 0.8;
}

/* Mobile Menu Styles */
.mobile-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  z-index: 999;
  display: none !important; /* Hidden by default on desktop */
  flex-direction: column;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  transform: translateX(100%);
}

.mobile-menu-open {
  opacity: 1;
  visibility: visible;
  transform: translateX(0);
}

.mobile-menu::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 20%, rgba(187, 134, 252, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(75, 192, 192, 0.1) 0%, transparent 50%);
  z-index: -1;
}

/* Header */
.mobile-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
}

/* Dark theme mobile menu header */
.dark .mobile-menu-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
}

/* Light theme mobile menu header */
.light .mobile-menu-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(0, 0, 0, 0.05);
}

.mobile-menu-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.4rem;
  color: #fff;
}

.mobile-menu-close {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

/* Dark theme mobile menu close */
.dark .mobile-menu-close {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Light theme mobile menu close */
.light .mobile-menu-close {
  background: rgba(60, 39, 142, 0.1);
  border: 1px solid rgba(60, 39, 142, 0.2);
}

.mobile-menu-close:hover {
  background: rgba(255, 67, 67, 0.2);
  border-color: #FF4343;
  transform: scale(1.1);
}

/* Navigation */
.mobile-menu-nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 30px 25px;
  gap: 15px;
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 18px 20px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  text-decoration: none;
  color: #fff;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.mobile-menu-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s;
}

/* Dark theme mobile menu item */
.dark .mobile-menu-item {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.dark .mobile-menu-item::before {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
}

/* Light theme mobile menu item */
.light .mobile-menu-item {
  background: rgba(60, 39, 142, 0.08);
  border: 1px solid rgba(60, 39, 142, 0.1);
}

.light .mobile-menu-item::before {
  background: linear-gradient(90deg, transparent, rgba(60, 39, 142, 0.1), transparent);
}

.mobile-menu-item:hover::before {
  left: 100%;
}

.mobile-menu-item:hover {
  background: rgba(187, 134, 252, 0.15);
  border-color: #BB86FC;
  transform: translateX(5px);
  box-shadow: 0 5px 20px rgba(187, 134, 252, 0.3);
}

.mobile-menu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  flex-shrink: 0;
}

/* Dark theme mobile menu icon */
.dark .mobile-menu-icon {
  background: rgba(255, 255, 255, 0.1);
}

/* Light theme mobile menu icon */
.light .mobile-menu-icon {
  background: rgba(60, 39, 142, 0.1);
}

/* Actions */
.mobile-menu-actions {
  padding: 25px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Dark theme mobile menu actions */
.dark .mobile-menu-actions {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
}

/* Light theme mobile menu actions */
.light .mobile-menu-actions {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(0, 0, 0, 0.05);
}

.mobile-menu-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
}

.mobile-menu-btn-login {
  background: rgba(187, 134, 252, 0.1);
  color: #BB86FC;
  border: 1px solid rgba(187, 134, 252, 0.3);
}

.mobile-menu-btn-register {
  background: linear-gradient(135deg, #BB86FC, #9C27B0);
  color: #fff;
}

.mobile-menu-btn-theme {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Dark theme mobile menu buttons */
.dark .mobile-menu-btn-login {
  background: rgba(187, 134, 252, 0.1);
  border: 1px solid rgba(187, 134, 252, 0.3);
}

.dark .mobile-menu-btn-theme {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Light theme mobile menu buttons */
.light .mobile-menu-btn-login {
  background: rgba(187, 134, 252, 0.1);
  border: 1px solid rgba(187, 134, 252, 0.3);
}

.light .mobile-menu-btn-theme {
  background: rgba(60, 39, 142, 0.1);
  border: 1px solid rgba(60, 39, 142, 0.2);
}

.mobile-menu-btn-login:hover {
  background: rgba(187, 134, 252, 0.2);
  border-color: #BB86FC;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(187, 134, 252, 0.3);
}

.mobile-menu-btn-register:hover {
  background: linear-gradient(135deg, #9C27B0, #BB86FC);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(187, 134, 252, 0.4);
}

.mobile-menu-btn-theme:hover {
  background: rgba(187, 134, 252, 0.2);
  border-color: #BB86FC;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(187, 134, 252, 0.3);
}



.animated-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.floating-shapes {
  position: relative;
  width: 100%;
  height: 100%;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(187, 134, 252, 0.1), rgba(75, 192, 192, 0.1));
  animation: float 20s infinite linear;
}

.shape-1 {
  width: 100px;
  height: 100px;
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.shape-2 {
  width: 150px;
  height: 150px;
  top: 20%;
  right: 15%;
  animation-delay: -5s;
}

.shape-3 {
  width: 80px;
  height: 80px;
  bottom: 30%;
  left: 20%;
  animation-delay: -10s;
}

.shape-4 {
  width: 120px;
  height: 120px;
  bottom: 20%;
  right: 10%;
  animation-delay: -15s;
}

.shape-5 {
  width: 60px;
  height: 60px;
  top: 50%;
  left: 50%;
  animation-delay: -7s;
}

@keyframes float {
  0%, 100% { 
    transform: translateY(0px) rotate(0deg);
    opacity: 0.3;
  }
  25% { 
    transform: translateY(-20px) rotate(90deg);
    opacity: 0.6;
  }
  50% { 
    transform: translateY(-40px) rotate(180deg);
    opacity: 0.3;
  }
  75% { 
    transform: translateY(-20px) rotate(270deg);
    opacity: 0.6;
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
}

.hero-section {
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  padding: 120px 0 100px 0;
}

.hero-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 50px;
  padding: 8px 16px;
  font-size: 0.9rem;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
}

.hero-title {
  font-size: 4rem;
  line-height: 1.1;
  margin-bottom: 20px;
  font-family: 'iranyekanB', sans-serif;
}

.gradient-text {
  background: linear-gradient(45deg, #BB86FC, #4BC0C0, #5584FF);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 3s ease-in-out infinite;
}

.hero-subtitle {
  background: linear-gradient(45deg, #FF6B6B, #4ECDC4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.hero-description {
  font-size: 1.3rem;
  line-height: 1.7;
  margin-bottom: 40px;
  opacity: 0.9;
  transition: color 0.3s ease;
}

/* Dark theme hero description */
.dark .hero-description {
  color: #e0e0e0;
}

/* Light theme hero description */
.light .hero-description {
  color: #666;
}

.hero-stats {
  display: flex;
  gap: 40px;
  margin-bottom: 40px;
}

.hero-stats .stat-item {
  text-align: center;
}

.hero-stats .stat-number {
  font-size: 1.5rem;
  color: #BB86FC;
  margin-bottom: 5px;
}

.hero-stats .stat-label {
  font-size: 0.9rem;
  opacity: 0.7;
  transition: color 0.3s ease;
}

/* Dark theme hero stats label */
.dark .hero-stats .stat-label {
  color: #e0e0e0;
}

/* Light theme hero stats label */
.light .hero-stats .stat-label {
  color: #666;
}

.hero-buttons {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.btn-primary, .btn-secondary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 32px;
  border-radius: 50px;
  font-size: 1rem;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'iranyekanB', sans-serif;
  position: relative;
  overflow: hidden;
}

.btn-primary {
  background: linear-gradient(45deg, #BB86FC, #4BC0C0);
  color: #fff;
  box-shadow: 0 10px 30px rgba(187, 134, 252, 0.3);
}

.btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 40px rgba(187, 134, 252, 0.4);
}

.btn-glow {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn-primary:hover .btn-glow {
  left: 100%;
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: #BB86FC;
  border: 2px solid rgba(187, 134, 252, 0.3);
  backdrop-filter: blur(10px);
}

.btn-secondary:hover {
  background: rgba(187, 134, 252, 0.1);
  border-color: #BB86FC;
  transform: translateY(-2px);
}

.hero-visual {
  position: relative;
  height: 600px;
}

.hero-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 30px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.main-card {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
  z-index: 2;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
}

.card-logo {
  width: 50px;
  height: 50px;
  background: linear-gradient(45deg, #BB86FC, #4BC0C0);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-title {
  font-size: 1.2rem;
}

.profit-indicator {
  display: flex;
  justify-content: center;
  margin-bottom: 25px;
}

.profit-circle {
  position: relative;
  width: 120px;
  height: 120px;
}

.profit-circle svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.progress-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.1);
  stroke-width: 8;
}

.progress-fill {
  fill: none;
  stroke: url(#gradient);
  stroke-width: 8;
  stroke-linecap: round;
  animation: progress 2s ease-out;
}

@keyframes progress {
  from { stroke-dashoffset: 251.2; }
  to { stroke-dashoffset: 75.36; }
}

.profit-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.profit-percent {
  display: block;
  font-size: 1.5rem;
  color: #BB86FC;
}

.profit-label {
  font-size: 0.9rem;
  opacity: 0.7;
}

.investment-info {
  display: flex;
  justify-content: space-between;
}

.info-item {
  text-align: center;
}

.info-item .label {
  display: block;
  font-size: 0.8rem;
  opacity: 0.7;
  margin-bottom: 5px;
}

.info-item .value {
  display: block;
  font-size: 1.1rem;    
  color: #4BC0C0;
}

.floating-card {
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: float 6s ease-in-out infinite;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

/* Dark theme floating card */
.dark .floating-card {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Light theme floating card */
.light .floating-card {
  background: rgba(60, 39, 142, 0.1);
  border: 1px solid rgba(60, 39, 142, 0.2);
}

.card-1 {
  top: 50px;
  left: 50px;
  animation-delay: 0s;
}

.card-2 {
  top: 200px;
  right: 50px;
  animation-delay: 2s;
}

.card-3 {
  bottom: 100px;
  left: 100px;
  animation-delay: 4s;
}

.card-text h4 {
  font-size: 1rem;
  margin: 0 0 5px 0;
  font-family: 'iranyekanB', sans-serif;
}

.card-text p {
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.8;
  transition: color 0.3s ease;
}

/* Dark theme floating card text */
.dark .card-text h4,
.dark .card-text p {
  color: #fff;
}

/* Light theme floating card text */
.light .card-text h4,
.light .card-text p {
  color: #333;
}

.services-section {
  padding: 120px 0;
  backdrop-filter: blur(20px);
  transition: all 0.3s ease;
}

/* Dark theme services */
.dark .services-section {
  background: rgba(26, 26, 46, 0.8);
}

/* Light theme services */
.light .services-section {
  background: rgba(255, 255, 255, 0.8);
}

.section-header {
  text-align: center;
  margin-bottom: 100px;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 50px;
  padding: 8px 16px;
  font-size: 0.9rem;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
}

.section-header h2 {
  font-size: 3rem;
  margin-bottom: 20px;
  font-family: 'iranyekanB', sans-serif;
  background: linear-gradient(45deg, #BB86FC, #4BC0C0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-header p {
  font-size: 1.2rem;
  opacity: 0.8;
  transition: color 0.3s ease;
}

/* Dark theme section header */
.dark .section-header p {
  color: #e0e0e0;
}

/* Light theme section header */
.light .section-header p {
  color: #666;
}

.services-showcase {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: start;
}

.service-main {
  position: relative;
}

.service-card {
  background: linear-gradient(135deg, rgba(187, 134, 252, 0.1), rgba(75, 192, 192, 0.1));
  border-radius: 30px;
  padding: 50px;
  border: 1px solid rgba(187, 134, 252, 0.2);
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
}

.service-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 30%, rgba(187, 134, 252, 0.1), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.service-card:hover::before {
  opacity: 1;
}

.service-visual {
  position: relative;
  margin-bottom: 40px;
  display: flex;
  justify-content: center;
}

.service-icon-large {
  width: 120px;
  height: 120px;
  background: linear-gradient(45deg, #BB86FC, #4BC0C0);
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  box-shadow: 0 20px 60px rgba(187, 134, 252, 0.3);
}

.service-animation {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.pulse-ring {
  position: absolute;
  width: 120px;
  height: 120px;
  border: 2px solid rgba(187, 134, 252, 0.3);
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.pulse-ring:nth-child(2) {
  animation-delay: 0.5s;
}

.pulse-ring:nth-child(3) {
  animation-delay: 1s;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

.service-content h3 {
  font-size: 2.2rem;
  margin-bottom: 20px;
  font-family: 'iranyekanB', sans-serif;
  background: linear-gradient(45deg, #BB86FC, #4BC0C0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.service-content p {
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 30px;
  opacity: 0.9;
  transition: color 0.3s ease;
}

/* Dark theme service content */
.dark .service-content p {
  color: #e0e0e0;
}

/* Light theme service content */
.light .service-content p {
  color: #666;
}

.service-stats {
  display: flex;
  gap: 40px;
  justify-content: center;
}

.stat {
  text-align: center;
}

.stat-value {
  display: block;
  font-size: 1.8rem;
  color: #BB86FC;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.9rem;
  opacity: 0.7;
  transition: color 0.3s ease;
}

/* Dark theme stat label */
.dark .stat-label {
  color: #e0e0e0;
}

/* Light theme stat label */
.light .stat-label {
  color: #666;
}

.services-sidebar {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.service-item {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 25px;
  display: flex;
  align-items: center;
  gap: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.service-item:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: #BB86FC;
  transform: translateX(10px);
  box-shadow: 0 10px 30px rgba(187, 134, 252, 0.2);
}

/* Dark theme service item */
.dark .service-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.dark .service-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Light theme service item */
.light .service-item {
  background: rgba(60, 39, 142, 0.05);
  border: 1px solid rgba(60, 39, 142, 0.1);
}

.light .service-item:hover {
  background: rgba(60, 39, 142, 0.1);
}

.service-icon {
  width: 60px;
  height: 60px;
  background: rgba(187, 134, 252, 0.1);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.service-info {
  flex: 1;
}

.service-info h4 {
  font-size: 1.2rem;
  margin-bottom: 8px;
  font-family: 'iranyekanB', sans-serif;
  transition: color 0.3s ease;
}

/* Dark theme service info h4 */
.dark .service-info h4 {
  color: #fff;
}

/* Light theme service info h4 */
.light .service-info h4 {
  color: #333;
}

.service-info p {
  font-size: 0.9rem;
  opacity: 0.7;
  margin: 0;
  transition: color 0.3s ease;
}

/* Dark theme service info */
.dark .service-info p {
  color: #e0e0e0;
}

/* Light theme service info */
.light .service-info p {
  color: #666;
}

.service-arrow {
  opacity: 0;
  transition: all 0.3s ease;
  transform: translateX(-10px);
}

.service-item:hover .service-arrow {
  opacity: 1;
  transform: translateX(0);
}


.download-section {
  padding: 120px 0;
  backdrop-filter: blur(20px);
  transition: all 0.3s ease;
}

/* Dark theme download */
.dark .download-section {
  background: rgba(26, 26, 46, 0.8);
}

/* Light theme download */
.light .download-section {
  background: rgba(255, 255, 255, 0.8);
}

.download-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
}

.download-text h2 {
  font-size: 3rem;
  margin-bottom: 25px;
  font-family: 'iranyekanB', sans-serif;
  background: linear-gradient(45deg, #BB86FC, #4BC0C0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.download-text p {
  font-size: 1.2rem;
  margin-bottom: 40px;
  opacity: 0.8;
  line-height: 1.7;
  transition: color 0.3s ease;
}

/* Dark theme download text */
.dark .download-text p {
  color: #e0e0e0;
}

/* Light theme download text */
.light .download-text p {
  color: #666;
}

.download-features {
  margin-bottom: 50px;
}

.download-feature {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  font-size: 1.1rem;
}

.download-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 35px;
  background: linear-gradient(45deg, #BB86FC, #4BC0C0);
  color: #fff;
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'iranyekanB', sans-serif;
  box-shadow: 0 15px 40px rgba(187, 134, 252, 0.3);
}

.download-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 25px 50px rgba(187, 134, 252, 0.4);
}

.phone-mockup {
  display: flex;
  justify-content: center;
}

.phone-screen {
  width: 300px;
  height: 600px;
  background: #000;
  border-radius: 35px;
  padding: 13px;
  position: relative;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}

.app-screenshot {
  width: 100%;
  height: 100%;
  border-radius: 20px;
  display: block;
}

.app-preview {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  height: 100%;
  border-radius: 25px;
  padding: 25px;
  color: #fff;
}

.app-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 35px;
}

.app-logo {
  width: 45px;
  height: 45px;
  background: #BB86FC;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
}

.balance-card {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
  margin-bottom: 35px;
  text-align: center;
  backdrop-filter: blur(10px);
}

.balance-amount {
  font-size: 2.2rem;
  margin: 10px 0 0 0;
  font-family: sans-serif;
  background: linear-gradient(45deg, #BB86FC, #4BC0C0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.quick-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.stats-section {
  padding: 100px 0;
  transition: all 0.3s ease;
}

/* Dark theme stats */
.dark .stats-section {
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%);
}

/* Light theme stats */
.light .stats-section {
  background: linear-gradient(135deg, #f7f4ff 0%, #e8e2ff 100%);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 50px;
  text-align: center;
}

.stat-item {
  position: relative;
}

.stat-icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
  backdrop-filter: blur(10px);
}

.stat-number {
  font-size: 3.5rem;
  margin-bottom: 15px;
  font-family: sans-serif;
  background: linear-gradient(45deg, #BB86FC, #4BC0C0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-label {
  font-size: 1.2rem;
  opacity: 0.9;
  color: #e0e0e0;
}

.cta-section {
  padding: 120px 0;
  backdrop-filter: blur(20px);
  text-align: center;
  transition: all 0.3s ease;
}

/* Dark theme CTA */
.dark .cta-section {
  background: rgba(26, 26, 46, 0.8);
}

/* Light theme CTA */
.light .cta-section {
  background: rgba(255, 255, 255, 0.8);
}

.cta-content h2 {
  font-size: 3.5rem;
  margin-bottom: 25px;
  font-family: 'iranyekanB', sans-serif;
  background: linear-gradient(45deg, #BB86FC, #4BC0C0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.cta-content p {
  font-size: 1.3rem;
  margin-bottom: 50px;
  opacity: 0.8;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  transition: color 0.3s ease;
}

/* Dark theme CTA content */
.dark .cta-content p {
  color: #e0e0e0;
}

/* Light theme CTA content */
.light .cta-content p {
  color: #666;
}

 .cta-buttons {
   display: flex;
   gap: 25px;
   justify-content: center;
   flex-wrap: wrap;
 }

 .about-section {
   padding: 120px 0;
   transition: all 0.3s ease;
 }

 /* Dark theme about */
 .dark .about-section {
   background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%);
 }

 /* Light theme about */
 .light .about-section {
   background: linear-gradient(135deg, #f7f4ff 0%, #e8e2ff 100%);
 }

 .about-content {
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 80px;
   align-items: center;
   margin-top: 80px;
 }

 .about-text h3 {
   font-size: 2rem;
   margin-bottom: 20px;
   font-family: 'iranyekanB', sans-serif;
   background: linear-gradient(45deg, #BB86FC, #4BC0C0);
   -webkit-background-clip: text;
   -webkit-text-fill-color: transparent;
   background-clip: text;
 }

 .about-text p {
   font-size: 1.1rem;
   line-height: 1.7;
   margin-bottom: 30px;
   opacity: 0.9;
   transition: color 0.3s ease;
 }

 /* Dark theme about text */
 .dark .about-text p {
   color: #e0e0e0;
 }

 /* Light theme about text */
 .light .about-text p {
   color: #666;
 }

 .about-text ul {
   list-style: none;
   padding: 0;
 }

 .about-text li {
   display: flex;
   align-items: center;
   gap: 15px;
   margin-bottom: 15px;
   font-size: 1rem;
   transition: color 0.3s ease;
 }

 /* Dark theme about text li */
 .dark .about-text li {
   color: #e0e0e0;
 }

 /* Light theme about text li */
 .light .about-text li {
   color: #666;
 }

 .about-text li::before {
   content: '✓';
   color: #4BC0C0;
   font-size: 1.2rem;
 }

 .about-stats {
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 30px;
   text-align: center;
 }

.about-stat {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 30px 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

/* Dark theme about stat */
.dark .about-stat {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Light theme about stat */
.light .about-stat {
  background: rgba(60, 39, 142, 0.05);
  border: 1px solid rgba(60, 39, 142, 0.1);
}

 .about-stat .stat-number {
   font-size: 2.5rem;
   margin-bottom: 10px;
   background: linear-gradient(45deg, #BB86FC, #4BC0C0);
   -webkit-background-clip: text;
   -webkit-text-fill-color: transparent;
   background-clip: text;
 }

   .about-stat .stat-label {
   font-size: 1rem;
   opacity: 0.8;
   transition: color 0.3s ease;
 }

 /* Dark theme about stat label */
 .dark .about-stat .stat-label {
   color: #e0e0e0;
 }

 /* Light theme about stat label */
 .light .about-stat .stat-label {
   color: #666;
 }

/* Footer Styles */
.footer {
  position: relative;
  transition: all 0.3s ease;
}

/* Dark theme footer */
.dark .footer {
  background: linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%);
  color: #fff;
}

/* Light theme footer */
.light .footer {
  background: linear-gradient(135deg, #f0f0f0 0%, #e8e2ff 100%);
  color: #333;
}

.footer-waves {
  position: absolute;
  top: -100px;
  left: 0;
  width: 100%;
  height: 100px;
  overflow: hidden;
}

.footer-waves svg {
  width: 100%;
  height: 100%;
}

.footer-content {
  position: relative;
  z-index: 2;
  padding: 80px 0 40px 0;
}

.footer-main {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 40px;
}



.footer-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  font-size: 1.8rem;
}

.footer-logo .logo-text {
  color: #BB86FC;
}

.footer-description {
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 30px;
  opacity: 0.8;
  transition: color 0.3s ease;
}

/* Dark theme footer description */
.dark .footer-description {
  color: #e0e0e0;
}

/* Light theme footer description */
.light .footer-description {
  color: #666;
}

.social-links {
  display: flex;
  gap: 15px;
}

.social-link {
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

/* Dark theme social link */
.dark .social-link {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Light theme social link */
.light .social-link {
  background: rgba(60, 39, 142, 0.1);
  border: 1px solid rgba(60, 39, 142, 0.1);
}

.social-link:hover {
  background: rgba(187, 134, 252, 0.2);
  border-color: #BB86FC;
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(187, 134, 252, 0.3);
}





.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 30px;
}

.footer-bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.copyright p {
  font-size: 0.9rem;
  opacity: 0.7;
  margin: 0;
  transition: color 0.3s ease;
}

/* Dark theme copyright */
.dark .copyright p {
  color: #e0e0e0;
}

/* Light theme copyright */
.light .copyright p {
  color: #666;
}



/* Desktop styles - ensure proper display */
@media (min-width: 769px) {
  .nav-menu {
    display: flex !important;
  }
  
  .header-actions {
    display: flex !important;
  }
  
  .mobile-menu-btn {
    display: none !important;
  }
  
  .mobile-menu {
    display: none !important;
  }
}

@media (max-width: 768px) {
  .header {
    width: 100%;
    left: 0;
    transform: none;
    border-radius: 0;
  }

  .header-container {
    padding: 0 20px;
  }

  .header-content {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 0;
  }

  .nav-menu {
    display: none;
  }

  .header-actions {
    display: none;
  }

  .mobile-menu-btn {
    display: block !important; /* Show only on mobile */
  }

  .mobile-menu {
    display: flex !important; /* Show only on mobile */
  }

  .hero-content {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 40px;
  }
  
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-description {
    font-size: 1.1rem;
  }
  
  .hero-stats {
    justify-content: center;
    gap: 30px;
  }
  
  .hero-buttons {
    justify-content: center;
  }
  
  .hero-visual {
    height: auto;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
  
  .main-card {
    width: 100%;
    max-width: 320px;
    padding: 20px;
    position: relative;
    transform: none;
    top: auto;
    left: auto;
  }
  
  .floating-card {
    position: relative;
    width: 100%;
    max-width: 280px;
    margin: 0;
    animation: none;
    transform: none;
  }
  
  .card-1, .card-2, .card-3 {
    position: relative;
    top: auto;
    left: auto;
    right: auto;
    bottom: auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px 20px;
  }
  
  .download-content {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 40px;
  }
  
  .download-text h2 {
    font-size: 2.2rem;
  }
  
  .services-showcase {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  
  .service-card {
    padding: 30px;
  }
  
  .service-content h3 {
    font-size: 1.8rem;
  }
  
  .service-stats {
    flex-direction: column;
    gap: 20px;
  }
  
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }
  
  .stat-number {
    font-size: 2.5rem;
  }
  
  .cta-content h2 {
    font-size: 2.5rem;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .about-content {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 40px;
  }
  
  .about-stats {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  .about-stat {
    padding: 20px 15px;
  }
  
  .about-stat .stat-number {
    font-size: 2rem;
  }
  
  /* Footer Mobile Styles */
  .footer-main {
    text-align: center;
  }
  
  .social-links {
    justify-content: center;
  }
  
  .footer-bottom-content {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 15px;
  }
  
  .header-container {
    padding: 0 15px;
  }
  
  .hero-title {
    font-size: 2rem;
  }

  .hero-description {
    font-size: 1rem;
  }
  
  .hero-buttons {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
  
  .btn-primary, .btn-secondary {
    width: 100%;
    max-width: 280px;
    justify-content: center;
  }
  
  .hero-stats {
    flex-direction: column;
    gap: 20px;
  }
  
  .hero-visual {
    height: auto;
    padding: 20px 0;
  }
  
  .main-card {
    width: 100%;
    max-width: 280px;
    padding: 15px;
  }
  
  .floating-card {
    padding: 15px;
    margin: 5px;
  }
  
  .card-text h4 {
    font-size: 0.9rem;
  }
  
  .card-text p {
    font-size: 0.8rem;
  }
  
  .section-header h2 {
    font-size: 1.8rem;
  }
  
  .section-header p {
    font-size: 1rem;
  }
  
  .service-content h3 {
    font-size: 1.5rem;
  }
  
  .service-content p {
    font-size: 1rem;
  }
  
  .service-item {
    padding: 20px;
    gap: 15px;
  }
  
  .service-icon {
    width: 50px;
    height: 50px;
  }
  
  .service-info h4 {
    font-size: 1.1rem;
  }
  
  .service-info p {
    font-size: 0.9rem;
  }
  
  
  .download-text h2 {
    font-size: 1.8rem;
  }
  
  .download-text p {
    font-size: 1rem;
  }
  
  .download-feature {
    font-size: 1rem;
    gap: 12px;
  }
  
  .download-btn {
    width: 100%;
    max-width: 280px;
    justify-content: center;
  }
  
  .phone-screen {
    width: 220px;
    height: 400px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .stat-number {
    font-size: 2.5rem;
  }
  
  .stat-label {
    font-size: 1rem;
  }
  
  .cta-content h2 {
    font-size: 2rem;
  }
  
  .cta-content p {
    font-size: 1.1rem;
  }
  
  .about-text h3 {
    font-size: 1.5rem;
  }
  
  .about-stat .stat-number {
    font-size: 1.8rem;
  }
}

@media (max-width: 360px) {
  .container {
    padding: 0 10px;
  }
  
  .header-container {
    padding: 0 10px;
  }

  .mobile-menu-header {
    padding: 12px 15px;
  }

  .mobile-menu-brand {
    font-size: 1.1rem;
  }

  .mobile-menu-nav {
    padding: 15px;
    gap: 10px;
  }

  .mobile-menu-item {
    padding: 12px 15px;
    font-size: 0.95rem;
  }

  .mobile-menu-icon {
    width: 32px;
    height: 32px;
  }

  .mobile-menu-actions {
    padding: 15px;
  }

  .mobile-menu-btn {
    padding: 12px 18px;
    font-size: 0.9rem;
  }
  
  .hero-title {
    font-size: 1.8rem;
  }
  
  .hero-description {
    font-size: 0.9rem;
  }
  
  .main-card {
    width: 220px;
    padding: 12px;
  }
  
  .card-header {
    gap: 10px;
    margin-bottom: 15px;
  }
  
  .card-logo {
    width: 40px;
    height: 40px;
  }
  
  .card-title {
    font-size: 1rem;
  }
  
  .profit-circle {
    width: 100px;
    height: 100px;
  }
  
  .profit-percent {
    font-size: 1.2rem;
  }
  
  .profit-label {
    font-size: 0.8rem;
  }
  
  .info-item .value {
    font-size: 1rem;
  }
  
  .info-item .label {
    font-size: 0.7rem;
  }
  
  .floating-card {
    padding: 12px;
    gap: 10px;
  }
  
  .card-text h4 {
    font-size: 0.8rem;
  }
  
  .card-text p {
    font-size: 0.7rem;
  }
  
  .section-header h2 {
    font-size: 1.8rem;
  }
  
  .service-card {
    padding: 20px;
  }
  
  .service-icon-large {
    width: 80px;
    height: 80px;
  }
  
  .service-content h3 {
    font-size: 1.3rem;
  }
  
  
  .phone-screen {
    width: 200px;
    height: 350px;
  }
  
  .download-btn {
    padding: 15px 25px;
    font-size: 1rem;
  }
  
  .cta-content h2 {
    font-size: 1.8rem;
  }
  
  .about-text h3 {
    font-size: 1.3rem;
  }
  
  .about-stat .stat-number {
    font-size: 1.8rem;
  }
}

/* Tablet Styles */
@media (min-width: 769px) and (max-width: 1024px) {
  .container {
    padding: 0 30px;
  }
  
  .header-container {
    padding: 0 5%;
  }
  
  .hero-content {
    gap: 60px;
  }
  
  .hero-title {
    font-size: 3.2rem;
  }
  
  .hero-visual {
    height: 500px;
  }
  
  .main-card {
    width: 280px;
  }
  
  .services-showcase {
    gap: 60px;
  }
  
  .service-card {
    padding: 40px;
  }
  
  
  .download-content {
    gap: 60px;
  }
  
  .about-content {
    gap: 60px;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Large Desktop Styles */
@media (min-width: 1200px) {
  .container {
    max-width: 1400px;
  }
  
  .hero-content {
    gap: 100px;
  }
  
  .hero-title {
    font-size: 4.5rem;
  }
  
  .hero-visual {
    height: 700px;
  }
  
  .main-card {
    width: 350px;
  }
  
  .services-showcase {
    gap: 100px;
  }
  
  
  .download-content {
    gap: 100px;
  }
  
  .about-content {
    gap: 100px;
  }
  
  .stats-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Landscape Mobile Styles */
@media (max-height: 500px) and (orientation: landscape) {
  .hero-section {
    min-height: auto;
    padding: 80px 0 60px 0;
  }
  
  .hero-content {
    gap: 30px;
  }
  
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-description {
    font-size: 1rem;
    margin-bottom: 20px;
  }
  
  .hero-stats {
    margin-bottom: 20px;
  }
  
  .hero-buttons {
    margin-bottom: 20px;
  }
  
  .hero-visual {
    height: 250px;
  }
  
  .main-card {
    width: 200px;
    padding: 15px;
  }
  
  .floating-card {
    padding: 10px;
    gap: 8px;
  }
  
  .card-text h4 {
    font-size: 0.8rem;
  }
  
  .card-text p {
    font-size: 0.7rem;
  }
}

/* High DPI Display Support */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .hero-card,
  .service-card,
  .plan-card,
  .floating-card {
    border-width: 0.5px;
  }
  
  .header {
    border-bottom-width: 0.5px;
  }
}

/* Print Styles */
@media print {
  .header,
  .animated-bg,
  .mobile-menu-btn,
  .download-btn,
  .btn-primary,
  .btn-secondary,
  
  .home-page {
    background: white !important;
    color: black !important;
  }
  
  .hero-section {
    min-height: auto;
    padding: 20px 0;
  }
  
  .hero-content {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .hero-visual {
    display: none;
  }
  
  .services-showcase {
    grid-template-columns: 1fr;
  }
  
  
  .download-content {
    grid-template-columns: 1fr;
  }
  
  .about-content {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
